import React, {useEffect} from "react";
import {Box, IconButton, Typography} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NewMaestroSessionIcon from "../icons/NewMaestroSessionIcon";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import StyledTooltip from "../tooltips/StyledTooltip";
import MaestroConversation from "../../pages/RexAITrustedAdvisor/MaestroConversation";
import MaestroSessionId from "../../pages/RexAITrustedAdvisor/MaestroSessionId";
import SessionList from "../../pages/RexAITrustedAdvisor/SessionList";
import {
  RexAITrustedAdvisorProvider,
  useRexAITrustedAdvisorContext,
} from "../../pages/RexAITrustedAdvisor/Context/RexAITrustedAdvisorProvider";
import {useSidebarLayout} from "./Context/SidebarProvider";
import {useRexAI} from "../../context/RexAIProvider";
import {capitalizeText} from "../../resources/utils/stringUtils";
import {useSidebarAgentStyles} from "./useSidebarAgentStyles";
import {sidebarWidth} from "../../resources/environment";
import {useIsMobile} from "../../resources/hooks/useIsMobile";
import {getSelectedActionFromScope} from "../actions/actionUtils";
import {DEFAULT_CONTEXT_CAPTION, DEFAULT_CONTEXT_TOOLTIP} from "../agent/useAgentPage";
import {contains, some} from "underscore";

const formatSubPage = (subPage) => (subPage ? capitalizeText(subPage) : subPage);

const buildAgentContextLabel = (scopeContext, addFormLabel) => {
  const baseLabel =
    scopeContext?.description ||
    (scopeContext ? `${scopeContext.name} (ID: ${scopeContext.id})` : undefined);
  const subPageSuffix = addFormLabel ?
    ` — ${addFormLabel}` :
    scopeContext?.subPage ?
      ` — ${formatSubPage(scopeContext.subPage)} tab` :
      "";
  const selectedAction = getSelectedActionFromScope(scopeContext);
  const selectedActionItem = selectedAction ?
    ` — ${selectedAction.name} (ID: ${selectedAction.id})` :
    "";
  return baseLabel ? `${baseLabel}${subPageSuffix}${selectedActionItem}` : addFormLabel;
};

export const AgentToggle = () => {
  const {
    agentSupportedOnPage,
    agentPanelState,
    setAgentPanelState,
    setAgentUiCompressed,
    shouldShowMaestro,
    agentSidebarWasExpanded,
    setAgentSidebarWasExpanded,
  } = useRexAI();
  const {currentSidebarWidth, isSidebarExpanded, setIsSidebarExpanded} = useSidebarLayout();
  const isMobile = useIsMobile();

  const agentPanelWidth =
    agentPanelState === "conversation" ?
      sidebarWidth.agentPanelConversation :
      agentPanelState === "list" ?
        sidebarWidth.agentPanelList :
        0;

  const {classes} = useSidebarAgentStyles({currentSidebarWidth, agentPanelWidth});

  // Narrowing the viewport hides the toggle, so close the panel to avoid stranding it open
  // with no way to dismiss it.
  useEffect(() => {
    if (!isMobile || agentPanelState === "closed") {
      return;
    }
    setAgentUiCompressed(false);
    setAgentPanelState("closed");
    setIsSidebarExpanded(agentSidebarWasExpanded);
  }, [isMobile, agentPanelState]);

  if (!agentSupportedOnPage || !shouldShowMaestro || isMobile) {
    return null;
  }

  return (
    <Box className={classes.toggleWrapper}>
      <StyledTooltip
        title={agentPanelState === "closed" ? "Show agent sessions" : "Hide agent sessions"}
        placement="right"
      >
        <IconButton
          size="small"
          className={classes.toggle}
          onClick={() => {
            if (agentPanelState !== "closed") {
              setAgentUiCompressed(false);
              setAgentPanelState("closed");
              setIsSidebarExpanded(agentSidebarWasExpanded);
            } else {
              setAgentSidebarWasExpanded(isSidebarExpanded);
              setIsSidebarExpanded(false);
              setAgentPanelState("conversation");
            }
          }}
        >
          <SmartToyOutlinedIcon fontSize="small" />
        </IconButton>
      </StyledTooltip>
    </Box>
  );
};

const AgentPanelInner = () => {
  const {setIsSidebarExpanded, isSidebarExpanded, currentSidebarWidth} = useSidebarLayout();
  const {
    scopeContext,
    addFormLabel,
    onAgentWrite,
    sessionId,
    agentUiCompressed,
    setAgentUiCompressed,
    setAgentPanelState,
    agentPanelState,
    pendingAutoSend,
    setPendingAutoSend,
    agentSidebarWasExpanded,
    setAgentSidebarWasExpanded,
    contextCaption,
    contextTooltip,
    messageContext,
    allowedUiFormFields,
  } = useRexAI();

  const agentContextLabel = buildAgentContextLabel(scopeContext, addFormLabel);

  const panelWidth =
    agentPanelState === "conversation" ?
      sidebarWidth.agentPanelConversation :
      sidebarWidth.agentPanelList;
  const {classes} = useSidebarAgentStyles({currentSidebarWidth, panelWidth});

  const {
    sessionBadges,
    createNewSession,
    messages,
    isCurrentRequest,
    isStopping,
    inputRef,
    error,
    sessions,
    handleSend,
  } = useRexAITrustedAdvisorContext();

  const showConversationView =
    !!sessionId || agentPanelState === "conversation" || (!!error && sessions.length === 0);

  const showCompressViewButton =
    messageContext?.pageType === "reportTemplate" &&
    contains(allowedUiFormFields, "reportTemplate");

  useEffect(() => {
    setAgentUiCompressed(showConversationView);
    setAgentPanelState(showConversationView ? "conversation" : "list");
  }, [showConversationView]);

  useEffect(() => {
    if (sessionId) {
      setAgentPanelState("conversation");
      setAgentSidebarWasExpanded(isSidebarExpanded);
      setIsSidebarExpanded(false);
    }
  }, [sessionId]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (!onAgentWrite || !lastMessage?.agentResult?.operations) {
      return;
    }
    const hasMcpWrite = some(
      lastMessage.agentResult.operations,
      (operation) => operation.status === "success" && !operation.tool?.startsWith("ui_"),
    );
    if (hasMcpWrite) {
      onAgentWrite();
    }
  }, [messages, onAgentWrite]);

  useEffect(() => {
    if (!isCurrentRequest && !isStopping && showConversationView) {
      inputRef.current?.focus();
    }
  }, [isCurrentRequest, isStopping, showConversationView, inputRef]);

  useEffect(() => {
    if (!pendingAutoSend) {
      return;
    }
    if (!showConversationView) {
      setAgentPanelState("conversation");
      setAgentSidebarWasExpanded(isSidebarExpanded);
      setIsSidebarExpanded(false);
      return;
    }
    if (isCurrentRequest) {
      return;
    }
    const messageToAutoSend = pendingAutoSend;
    setPendingAutoSend(null);
    handleSend(null, messageToAutoSend);
  }, [pendingAutoSend, showConversationView, isCurrentRequest]);

  const handleNewConversation = () => {
    setAgentSidebarWasExpanded(isSidebarExpanded);
    setAgentPanelState("conversation");
    createNewSession();
    setIsSidebarExpanded(false);
  };

  const handleReturnToSessionList = () => {
    setAgentPanelState("list");
    setAgentUiCompressed(false);
    createNewSession();
    setIsSidebarExpanded(agentSidebarWasExpanded);
  };

  return (
    <Box className={classes.panel}>
      {showConversationView ?
        (
          <>
            <Box className={classes.backHeader}>
              <StyledTooltip title="Back to sessions">
                <IconButton size="small" onClick={handleReturnToSessionList}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </StyledTooltip>
              {showCompressViewButton && (
                <StyledTooltip title={agentUiCompressed ? "Expand view" : "Compress view"}>
                  <IconButton size="small" onClick={() => setAgentUiCompressed((prev) => !prev)}>
                    {agentUiCompressed ?
                      (
                        <VisibilityIcon fontSize="small" />
                      ) :
                      (
                        <VisibilityOffIcon fontSize="small" />
                      )}
                  </IconButton>
                </StyledTooltip>
              )}
              {agentContextLabel && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {contextCaption || DEFAULT_CONTEXT_CAPTION}
                  </Typography>
                  <StyledTooltip
                    title={`${contextTooltip || DEFAULT_CONTEXT_TOOLTIP} ${agentContextLabel}`}
                    placement="right"
                  >
                    <InfoOutlinedIcon color="primary" className={classes.contextIcon} />
                  </StyledTooltip>
                </>
              )}
              <Box className={classes.headerActions}>
                <MaestroSessionId sessionId={sessionId} />
                <StyledTooltip title="New session">
                  <IconButton size="small" onClick={handleNewConversation} disableRipple>
                    <NewMaestroSessionIcon fontSize="small" />
                  </IconButton>
                </StyledTooltip>
              </Box>
            </Box>
            <Box display="flex" height="calc(100vh - 40px)">
              <MaestroConversation
                placeholder="Ask Maestro..."
                inputId="sidebar-agent-prompt-input"
              />
            </Box>
          </>
        ) :
        (
          <Box pl={1} className={classes.sessionListBox}>
            <SessionList sessionBadges={sessionBadges} onCreateNew={handleNewConversation} />
          </Box>
        )}
    </Box>
  );
};

export const AgentPanel = () => {
  const {agentSupportedOnPage, agentPanelState, shouldShowMaestro} = useRexAI();
  const isMobile = useIsMobile();
  if (!agentSupportedOnPage || agentPanelState === "closed" || !shouldShowMaestro || isMobile) {
    return null;
  }
  return (
    <RexAITrustedAdvisorProvider>
      <AgentPanelInner />
    </RexAITrustedAdvisorProvider>
  );
};

const SidebarAgent = () => {
  return (
    <>
      <AgentToggle />
      <AgentPanel />
    </>
  );
};

export default SidebarAgent;
